import { db } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import {
  InspectionSchedule,
  RecurrencePattern,
  DayOfWeek,
  InspectionPriority,
} from '@easydone/shared';

/**
 * Schedule creation data
 */
export interface CreateScheduleData {
  name: string;
  description?: string;
  assetId: string;
  siteId: string;
  templateId: string;
  startDate: Date;
  recurrence: RecurrencePattern;
  priority: InspectionPriority;
  assignedTo?: string;
  leadTime?: number;
  approvalRequired?: boolean;
  approvalLevel?: string;
  approvalUserId?: string;
}

/**
 * Get all schedules for an organization
 */
export async function getSchedules(orgId: string): Promise<InspectionSchedule[]> {
  const schedulesRef = collection(db, 'organizations', orgId, 'schedules');
  const q = query(schedulesRef, orderBy('name', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as InspectionSchedule));
}

/**
 * Get active schedules for an organization
 */
export async function getActiveSchedules(orgId: string): Promise<InspectionSchedule[]> {
  const schedulesRef = collection(db, 'organizations', orgId, 'schedules');
  const q = query(schedulesRef, where('isActive', '==', true), orderBy('nextInspectionDate', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as InspectionSchedule));
}

/**
 * Get schedules for a specific asset
 */
export async function getAssetSchedules(orgId: string, assetId: string): Promise<InspectionSchedule[]> {
  const schedulesRef = collection(db, 'organizations', orgId, 'schedules');
  const q = query(schedulesRef, where('assetId', '==', assetId), orderBy('nextInspectionDate', 'asc'));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as InspectionSchedule));
}

/**
 * Get a single schedule by ID
 */
export async function getSchedule(orgId: string, scheduleId: string): Promise<InspectionSchedule | null> {
  const scheduleRef = doc(db, 'organizations', orgId, 'schedules', scheduleId);
  const snapshot = await getDoc(scheduleRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as InspectionSchedule;
}

/**
 * Create a new inspection schedule
 */
export async function createSchedule(
  orgId: string,
  userId: string,
  data: CreateScheduleData
): Promise<InspectionSchedule> {
  const schedulesRef = collection(db, 'organizations', orgId, 'schedules');

  // First inspection should be on the start date
  const startDateTimestamp = Timestamp.fromDate(data.startDate);

  const scheduleData = {
    orgId,
    name: data.name,
    description: data.description || '',
    assetId: data.assetId,
    siteId: data.siteId,
    templateId: data.templateId,
    startDate: startDateTimestamp,
    recurrence: data.recurrence,
    priority: data.priority,
    ...(data.assignedTo && { assignedTo: data.assignedTo }),
    leadTime: data.leadTime || 0,
    ...(data.approvalRequired !== undefined && { approvalRequired: data.approvalRequired }),
    ...(data.approvalLevel && { approvalLevel: data.approvalLevel }),
    ...(data.approvalUserId && { approvalUserId: data.approvalUserId }),
    isActive: true,
    nextInspectionDate: startDateTimestamp,
    createdBy: userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  const docRef = await addDoc(schedulesRef, scheduleData);
  const snapshot = await getDoc(docRef);

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as InspectionSchedule;
}

/**
 * Update an inspection schedule
 */
export async function updateSchedule(
  orgId: string,
  scheduleId: string,
  data: Partial<CreateScheduleData>
): Promise<void> {
  const scheduleRef = doc(db, 'organizations', orgId, 'schedules', scheduleId);

  // Build update object, filtering out undefined values
  const updateData: any = {
    updatedAt: serverTimestamp(),
  };

  // Only include defined fields
  if (data.name !== undefined) updateData.name = data.name;
  if (data.description !== undefined) updateData.description = data.description;
  if (data.assetId !== undefined) updateData.assetId = data.assetId;
  if (data.siteId !== undefined) updateData.siteId = data.siteId;
  if (data.templateId !== undefined) updateData.templateId = data.templateId;
  if (data.recurrence !== undefined) updateData.recurrence = data.recurrence;
  if (data.priority !== undefined) updateData.priority = data.priority;
  if (data.assignedTo !== undefined) updateData.assignedTo = data.assignedTo;
  if (data.leadTime !== undefined) updateData.leadTime = data.leadTime;
  if (data.approvalRequired !== undefined) updateData.approvalRequired = data.approvalRequired;
  if (data.approvalLevel !== undefined) updateData.approvalLevel = data.approvalLevel;
  if (data.approvalUserId !== undefined) updateData.approvalUserId = data.approvalUserId;

  // If startDate or recurrence changed, recalculate next inspection date
  if (data.startDate || data.recurrence) {
    const currentSchedule = await getSchedule(orgId, scheduleId);
    if (currentSchedule) {
      const startDate = data.startDate
        ? Timestamp.fromDate(data.startDate)
        : (typeof currentSchedule.startDate === 'number'
            ? Timestamp.fromMillis(currentSchedule.startDate)
            : currentSchedule.startDate);

      updateData.nextInspectionDate = startDate;
    }
  }

  if (data.startDate) {
    updateData.startDate = Timestamp.fromDate(data.startDate);
  }

  await updateDoc(scheduleRef, updateData);
}

/**
 * Toggle schedule active status
 */
export async function toggleScheduleActive(
  orgId: string,
  scheduleId: string,
  isActive: boolean
): Promise<void> {
  const scheduleRef = doc(db, 'organizations', orgId, 'schedules', scheduleId);
  await updateDoc(scheduleRef, {
    isActive,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete an inspection schedule
 */
export async function deleteSchedule(orgId: string, scheduleId: string): Promise<void> {
  const scheduleRef = doc(db, 'organizations', orgId, 'schedules', scheduleId);
  await deleteDoc(scheduleRef);
}

/**
 * Calculate the next inspection date based on start date and recurrence pattern
 */
export function calculateNextInspectionDate(
  startDate: any,
  recurrence: RecurrencePattern,
  fromDate?: any
): Timestamp {
  // Convert to Date, handling various timestamp formats
  const toDate = (ts: any): Date => {
    if (!ts) return new Date();
    if (ts instanceof Date) return ts;
    if (typeof ts === 'number') return new Date(ts);
    if (ts.toDate) return ts.toDate();
    if (ts.seconds) return new Date(ts.seconds * 1000);
    return new Date();
  };

  const baseDate = fromDate ? toDate(fromDate) : toDate(startDate);
  const nextDate = new Date(baseDate);

  switch (recurrence.frequency) {
    case 'daily':
      nextDate.setDate(nextDate.getDate() + recurrence.interval);
      break;

    case 'weekly':
      nextDate.setDate(nextDate.getDate() + (recurrence.interval * 7));
      // If specific days of week are set, find the next occurrence
      if (recurrence.daysOfWeek && recurrence.daysOfWeek.length > 0) {
        nextDate.setDate(nextDate.getDate() + findNextDayOfWeek(nextDate, recurrence.daysOfWeek));
      }
      break;

    case 'monthly':
      nextDate.setMonth(nextDate.getMonth() + recurrence.interval);
      // If specific day of month is set, use it
      if (recurrence.dayOfMonth) {
        nextDate.setDate(Math.min(recurrence.dayOfMonth, getDaysInMonth(nextDate)));
      }
      break;

    case 'yearly':
      nextDate.setFullYear(nextDate.getFullYear() + recurrence.interval);
      // If specific month is set, use it
      if (recurrence.monthOfYear) {
        nextDate.setMonth(recurrence.monthOfYear - 1);
      }
      if (recurrence.dayOfMonth) {
        nextDate.setDate(Math.min(recurrence.dayOfMonth, getDaysInMonth(nextDate)));
      }
      break;

    case 'custom':
      // For custom frequency, use the interval as days
      nextDate.setDate(nextDate.getDate() + recurrence.interval);
      break;
  }

  return Timestamp.fromDate(nextDate);
}

/**
 * Find days until next occurrence of specified day of week
 */
function findNextDayOfWeek(fromDate: Date, daysOfWeek: DayOfWeek[]): number {
  const dayMap: Record<DayOfWeek, number> = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  const currentDay = fromDate.getDay();
  const targetDays = daysOfWeek.map(d => dayMap[d]).sort((a, b) => a - b);

  // Find the next target day
  for (const targetDay of targetDays) {
    if (targetDay > currentDay) {
      return targetDay - currentDay;
    }
  }

  // If no day found this week, use first target day next week
  return 7 - currentDay + targetDays[0];
}

/**
 * Get number of days in a month
 */
function getDaysInMonth(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

/**
 * Check if a schedule should generate an inspection
 */
export function shouldGenerateInspection(schedule: InspectionSchedule): boolean {
  if (!schedule.isActive) return false;
  if (!schedule.nextInspectionDate) return true;

  const now = new Date();
  const nextDate = typeof schedule.nextInspectionDate === 'number'
    ? new Date(schedule.nextInspectionDate)
    : (schedule.nextInspectionDate as any).toDate();
  const leadTimeDays = schedule.leadTime || 0;

  // Generate inspection if we're within lead time of the next scheduled date
  const generateDate = new Date(nextDate);
  generateDate.setDate(generateDate.getDate() - leadTimeDays);

  return now >= generateDate;
}

/**
 * Check if schedule has reached its end
 */
export function isScheduleExpired(schedule: InspectionSchedule): boolean {
  // Check end date
  if (schedule.recurrence.endDate) {
    const now = new Date();
    const endDate = typeof schedule.recurrence.endDate === 'number'
      ? new Date(schedule.recurrence.endDate)
      : (schedule.recurrence.endDate as any).toDate();
    if (now > endDate) return true;
  }

  // Check max occurrences (would need to track generated count)
  // This would require adding a generatedCount field to the schedule

  return false;
}

/**
 * Generate an inspection from a schedule
 */
export async function generateInspectionFromSchedule(
  schedule: InspectionSchedule,
  userId: string,
  userName: string
): Promise<string> {
  const { createInspection } = await import('./inspectionService');

  const nextDate = schedule.nextInspectionDate
    ? (typeof schedule.nextInspectionDate === 'number'
        ? new Date(schedule.nextInspectionDate)
        : (schedule.nextInspectionDate as any).toDate())
    : undefined;

  // Convert old schedule model to new inspection model
  const inspectionData = {
    targetType: schedule.assetId ? ('asset' as const) : ('floc' as const),
    targetId: schedule.assetId || schedule.siteId, // Fallback to siteId for old schedules
    assetId: schedule.assetId,
    flocId: schedule.siteId, // Use old siteId as flocId for backward compatibility
    activityClass: 'inspection' as const, // Scheduled inspections are assessment only
    templateId: schedule.templateId,
    priority: schedule.priority,
    assignedTo: schedule.assignedTo,
    scheduledDate: nextDate,
    dueDate: nextDate,
    scheduleId: schedule.id,
    approvalRequired: schedule.approvalRequired,
    approvalLevel: schedule.approvalLevel,
    approvalUserId: schedule.approvalUserId,
  };

  const inspection = await createInspection(schedule.orgId, inspectionData, userId, userName);

  // Update schedule with new next inspection date
  const startDateTimestamp = typeof schedule.startDate === 'number'
    ? Timestamp.fromMillis(schedule.startDate)
    : schedule.startDate;

  const nextInspectionTimestamp = schedule.nextInspectionDate
    ? (typeof schedule.nextInspectionDate === 'number'
        ? Timestamp.fromMillis(schedule.nextInspectionDate)
        : schedule.nextInspectionDate)
    : undefined;

  const newNextDate = calculateNextInspectionDate(
    startDateTimestamp,
    schedule.recurrence,
    nextInspectionTimestamp
  );

  const scheduleRef = doc(db, 'organizations', schedule.orgId, 'schedules', schedule.id);
  await updateDoc(scheduleRef, {
    lastGeneratedDate: serverTimestamp(),
    nextInspectionDate: newNextDate,
    updatedAt: serverTimestamp(),
  });

  return inspection.id;
}

/**
 * Process all active schedules and generate inspections as needed
 * This should be called periodically (e.g., daily via a scheduled function)
 */
export async function processSchedules(
  orgId: string,
  systemUserId: string = 'system',
  systemUserName: string = 'System'
): Promise<{
  generated: number;
  errors: Array<{ scheduleId: string; error: string }>;
}> {
  const activeSchedules = await getActiveSchedules(orgId);
  const results = {
    generated: 0,
    errors: [] as Array<{ scheduleId: string; error: string }>,
  };

  for (const schedule of activeSchedules) {
    try {
      // Check if schedule is expired
      if (isScheduleExpired(schedule)) {
        await toggleScheduleActive(orgId, schedule.id, false);
        continue;
      }

      // Check if we should generate an inspection
      if (shouldGenerateInspection(schedule)) {
        await generateInspectionFromSchedule(schedule, systemUserId, systemUserName);
        results.generated++;
      }
    } catch (error: any) {
      console.error(`Failed to process schedule ${schedule.id}:`, error);
      results.errors.push({
        scheduleId: schedule.id,
        error: error.message || 'Unknown error',
      });
    }
  }

  return results;
}
